import { ComponentInstanceIcon } from '@radix-ui/react-icons'

import {
  BlockMetadata,
  blockMetadata,
} from '~/replicache/frontend/block-metadata'

import { type CommandCategory } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/slash-command'
import {
  Block,
  blockTypesRecord,
  type BlockType,
  type ComponentBlock,
} from '@basehub/data/replicache/index'
import { AIIcon } from '~/components/icons/ai'
import { ENABLE_SCHEMA_ASSISTANT } from '~/app/api/ai/schema-assistant/_constants'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { BlockSwap } from '@basehub/data/replicache/mutators/repo-space/block/mutators/change'
import { SYSTEM_AGENTS } from '@basehub/data/replicache/schema/block/agents'
import { getId } from '@basehub/utils/id/index'

type GetCommandsOpts = {
  hide?: Partial<Record<Block['type'], boolean>>
  existingComponents: Array<ComponentBlock>
}

export const getCommands = ({ hide, existingComponents }: GetCommandsOpts) => {
  const layoutCommands = []
  if (!hide?.list) {
    layoutCommands.push(
      getFromBlockMetadata(
        blockTypesRecord.list,
        ['list', 'collection', 'table', 'album', 'collection', 'posts', 'blog'],
        (b) => 'layout' in b && b.layout === 'table'
      ),
      getFromBlockMetadata(
        blockTypesRecord.list,
        ['album', 'collection', 'gallery', 'grid'],
        (b) => 'layout' in b && b.layout === 'gallery'
      ),
      getFromBlockMetadata(
        blockTypesRecord.list,
        ['list', 'collection', 'inline'],
        (b) => 'layout' in b && b.layout === 'inline'
      )
    )
  }
  if (!hide?.document) {
    layoutCommands.push(
      getFromBlockMetadata(blockTypesRecord.document, ['page', 'homepage'])
    )
  }

  const commands: CommandCategory[] = [
    ...(ENABLE_SCHEMA_ASSISTANT
      ? [
          {
            id: 'ai',
            name: '',
            commands: [
              {
                id: 'ai-command',
                title: 'AI',
                description: 'Ask AI to model your content',
                icon: <AIIcon />,
                command: (props) => {
                  props.onSelectBlock('ai')
                },
              },
            ],
          } satisfies CommandCategory,
        ]
      : []),
    {
      id: 'components',
      name: '',
      commands: [
        getFromBlockMetadata('component'),
        ...existingComponents.map((c) => {
          const title =
            c.typeOptions.displayTitle || c.title || 'Untitled Component'
          return {
            id: idToSourceBlockId(c),
            title:
              c.typeOptions.displayTitle || c.title || 'Untitled Component',
            description:
              c.typeOptions.displayDescription ||
              `Create an instance of "${title}"`,
            icon: c.typeOptions.displayIcon || <ComponentInstanceIcon />,
            ...(c.typeOptions.displayImage
              ? { preview: c.typeOptions.displayImage }
              : {}),
            command: (props) => {
              props.onSelectBlock(
                { newBlockType: 'instance' },
                { targetBlockId: idToSourceBlockId(c) }
              )
              props.onExit()
            },
          } satisfies CommandCategory['commands'][number]
        }),
      ],
    },
    {
      id: 'layout',
      name: 'Layout',
      commands: layoutCommands,
    },
    {
      id: 'primitives',
      name: 'Basic blocks',
      commands: [
        getFromBlockMetadata('text', [
          'title',
          'paragraph',
          'heading',
          'name',
          'url',
          'label',
          'href',
        ]),
        getFromBlockMetadata('select', [
          'pick',
          'options',
          'tags',
          'multi',
          'enum',
        ]),
        getFromBlockMetadata('reference', [
          'link',
          'target',
          'sections',
          'union',
          'switch',
        ]),
        getFromBlockMetadata('event', ['analytics', 'track', 'form', 'chart']),
        ...(!hide?.['workflow']
          ? [
              getFromBlockMetadata(
                'workflow',
                [
                  'automation',
                  'trigger',
                  'action',
                  'newsletter',
                  'email',
                  'mail',
                  'webhook',
                ],
                (b) => 'preset' in b && b.preset === 'all'
              ),
              getFromBlockMetadata(
                'workflow',
                [
                  'ai',
                  'autofill',
                  'automation',
                  'trigger',
                  'action',
                  'newsletter',
                  'email',
                  'mail',
                  'webhook',
                ],
                (b) => 'preset' in b && b.preset === 'ai'
              ),
            ]
          : []),
        ...(!hide?.['variant-set']
          ? [
              getFromBlockMetadata('variant-set', [
                'language',
                'locale',
                'version',
                'i18n',
                'internationalization',
              ]),
            ]
          : []),
        getFromBlockMetadata('number', ['count']),
        getFromBlockMetadata('color', [
          'colour',
          'chroma',
          'hue',
          'rgb',
          'hex',
        ]),
        getFromBlockMetadata('rich-text', [
          'body',
          'content',
          'article',
          'subtitle',
          'description',
        ]),
        getFromBlockMetadata('date', ['time']),
        getFromBlockMetadata('boolean', ['checkbox']),
        getFromBlockMetadata('icon', [
          'emoji',
          'symbol',
          'svg',
          'path',
          'logo',
          'arrow',
          'cross',
          'chevron',
          'hamburger',
        ]),
        getFromBlockMetadata('code-snippet'),
      ],
    },
    {
      id: 'media',
      name: 'Media',
      commands: [
        getFromBlockMetadata(
          'media',
          [
            'picture',
            'photo',
            'avatar',
            'cover',
            'logo',
            'png',
            'jpg',
            'avif',
            'gif',
          ],
          (b) => 'preset' in b && b.preset === 'all'
        ),
        getFromBlockMetadata(
          'media',
          [
            'picture',
            'photo',
            'avatar',
            'cover',
            'logo',
            'png',
            'jpg',
            'avif',
            'gif',
          ],
          (b) => 'preset' in b && b.preset === 'image'
        ),
        getFromBlockMetadata('og-image', [
          'image',
          'open graph',
          'metatags',
          'og',
          'social',
          'card',
        ]),
        getFromBlockMetadata(
          'media',
          ['mp4'],
          (b) => 'preset' in b && b.preset === 'video'
        ),
        getFromBlockMetadata(
          'media',
          ['mp3', 'song', 'music', 'voice', 'sound'],
          (b) => 'preset' in b && b.preset === 'audio'
        ),
        getFromBlockMetadata(
          'media',
          ['attachment', 'pdf', 'zip', 'doc', 'csv', 'woff', 'font'],
          (b) => 'preset' in b && b.preset === 'file'
        ),
      ],
    },
  ]

  return commands
}

export const getFromBlockMetadata = <T extends BlockType>(
  type: T,
  keywords?: string[],
  pick?: (arg: BlockMetadata) => boolean
) => {
  const items = blockMetadata.filter((b) => b.type === type)
  if (!items.length) throw new Error(`No block metadata for type ${type}`)
  const item = pick ? items.find(pick) : items[0]
  if (!item) throw new Error(`No block metadata for refine picked type ${type}`)
  if (item.hide) throw new Error(`Block metadata for type ${type} is hidden`)

  return {
    id: item.type,
    title: item.label,
    description: item.description,
    icon: item.icon,
    isNew: item.isNew,
    keywords,
    command: ((props) => {
      let swapParameters: BlockSwap
      if (item.type === 'agent') {
        throw new Error('Cannot swap to agent block type directly')
      }
      if (item.type === 'list') {
        swapParameters = {
          newBlockType: 'list',
          columnIds: [],
          layout: item.layout ?? 'table',
        }
      } else if (item.type === 'media') {
        swapParameters = {
          newBlockType: 'media',
          preset: item.preset,
        }
      } else if (item.type === 'workflow' && item.preset === 'ai') {
        swapParameters = {
          newBlockType: 'workflow',
          value: {
            trigger: {
              type: 'layout-block',
              blockIds: [],
              events: ['updated'],
              layoutBlockId: null,
            },
            actions: {
              [getId()]: {
                type: 'ai-autofill',
                blockId: null,
                agentId: SYSTEM_AGENTS['0'].id,
                prompt: '',
              },
            },
            activeRuns: [],
          },
        }
      } else {
        swapParameters = {
          newBlockType: item.type,
        }
      }

      props.onSelectBlock(swapParameters)
      props.onExit()
    }) satisfies CommandCategory['commands'][number]['command'],
  }
}
